import { useEffect, useMemo, useState } from 'react';
import { usePlugins } from 'domains/reporter/RichTextEditor/hooks';
import { useMacros } from 'domains/reporter/Template/useMacros';
import { computeMacroPreview, type MacroPreviewData } from './useMacroPreview';

const previewCache = new Map<string, MacroPreviewData>();
let objectIdentityCache = new WeakMap<object, number>();
const macrosVersionCache = new Map<string, number>();
let nextObjectIdentity = 1;
let nextMacrosVersion = 1;
const MAX_PREVIEW_CACHE_SIZE = 200;

const getObjectIdentity = (value: object): number => {
  const existingIdentity = objectIdentityCache.get(value);
  if (existingIdentity != null) {
    return existingIdentity;
  }

  const newIdentity = nextObjectIdentity;
  nextObjectIdentity += 1;
  objectIdentityCache.set(value, newIdentity);
  return newIdentity;
};

const getPreviewCacheKey = (
  referenceID: string,
  macros: ReturnType<typeof useMacros>,
  editorStateEnhancers: ReturnType<ReturnType<typeof usePlugins>['getEditorStateEnhancers']>
): string => {
  const macrosSignature = JSON.stringify(macros);
  let macrosVersion = macrosVersionCache.get(macrosSignature);
  if (macrosVersion == null) {
    macrosVersion = nextMacrosVersion;
    nextMacrosVersion += 1;
    macrosVersionCache.set(macrosSignature, macrosVersion);
  }

  const editorStateEnhancersVersion = editorStateEnhancers
    .map((enhancer) => getObjectIdentity(enhancer))
    .join(',');
  return `${referenceID}:${macrosVersion}:${editorStateEnhancersVersion}`;
};

const setPreviewCacheValue = (cacheKey: string, preview: MacroPreviewData): void => {
  previewCache.set(cacheKey, preview);
  while (previewCache.size > MAX_PREVIEW_CACHE_SIZE) {
    const oldestCacheKey = previewCache.keys().next().value;
    if (oldestCacheKey == null) {
      return;
    }
    previewCache.delete(oldestCacheKey);
  }
};

export type DeferredMacroPreviewOptions = {
  isActive?: boolean;
  loadDelayMs?: number;
};

export type DeferredMacroPreviewStatus = 'idle' | 'loading' | 'ready';

export type DeferredMacroPreviewState = {
  status: DeferredMacroPreviewStatus;
  data: Nullable<MacroPreviewData>;
};

export const __resetDeferredMacroPreviewCacheForTests = (): void => {
  previewCache.clear();
  macrosVersionCache.clear();
  objectIdentityCache = new WeakMap<object, number>();
  nextObjectIdentity = 1;
  nextMacrosVersion = 1;
};

export const useDeferredMacroPreview = (
  referenceID: Nullable<string>,
  { isActive = true, loadDelayMs = 0 }: DeferredMacroPreviewOptions = {}
): DeferredMacroPreviewState => {
  const macros = useMacros();
  const { getEditorStateEnhancers } = usePlugins();
  const editorStateEnhancers = useMemo(() => getEditorStateEnhancers(), [getEditorStateEnhancers]);

  const [deferredResult, setDeferredResult] = useState<DeferredMacroPreviewState>({
    status: 'idle',
    data: null,
  });

  useEffect(() => {
    if (!isActive || referenceID == null) {
      setDeferredResult({
        status: 'idle',
        data: null,
      });
      return;
    }

    const cacheKey = getPreviewCacheKey(referenceID, macros, editorStateEnhancers);
    const cachedPreview = previewCache.get(cacheKey);
    if (cachedPreview != null) {
      setDeferredResult({
        status: 'ready',
        data: cachedPreview,
      });
      return;
    }

    setDeferredResult({
      status: 'loading',
      data: null,
    });
    const timeoutID = window.setTimeout(() => {
      const preview = computeMacroPreview({
        referenceID,
        macros,
        editorStateEnhancers,
      });
      setPreviewCacheValue(cacheKey, preview);
      setDeferredResult({
        status: 'ready',
        data: preview,
      });
    }, loadDelayMs);

    return () => window.clearTimeout(timeoutID);
  }, [isActive, loadDelayMs, referenceID, macros, editorStateEnhancers]);

  return deferredResult;
};
